import { onUnmounted, watch } from "vue"
import { useTokenManager } from "@webapp/auth/TokenManager"
import { Environment } from "@webapp/util/Environment"
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr'
import { AsyncData, LoadMode } from '@webapp/util/AsyncData'
import { DataGroupMember } from './GroupAdminModel'
import { useUserStore } from './UserStore'
import { useDataGroupMemberStore } from "./DataGroupMemberStore"
import { useDataGroupStore } from "./DataGroupStore"
import { useFamilyGroupStore } from "./FamilyGroupStore"
import { isDefined } from "@webapp/util/TypeScriptUtil"
import { DateTime } from "luxon"
import { ItemAddedNotificationArgs } from "@webapp/util/Api"
import { useFamilyGroupMemberStore } from "./FamilyGroupMemberStore"

const gpUrl = Environment.get('GROUP_ADMIN_URL')

let notificationHub: HubConnection | undefined;

type Listener = (args?: any) => void
const listenerSets = new Map<string, Set<Listener>>()

export function useGroupAdminNotifications() {
    // NOTE: this is the only state that is local to this "use" call
    const localListeners: Listener[] = []

    function initialize() {
        if (notificationHub) {
            console.warn('GroupAdminNotifications already initialized')
            return
        }

        // NOTE: these will all be captured by closures below, so will have the same lifetime as the instance
        // on which this function was called
        const tokenManager = useTokenManager()
        const userStore = useUserStore()
        const dataGroupMemberStore = useDataGroupMemberStore()
        const dataGroupStore = useDataGroupStore()
        const familyGroupStore = useFamilyGroupStore()
        const familyGroupMemberStore = useFamilyGroupMemberStore()
    
        notificationHub = new HubConnectionBuilder()
            .withUrl(`${gpUrl}notifications`, {
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
                accessTokenFactory: async () => (await tokenManager.getAccessTokenAsync()) ?? '',
            })
            .withAutomaticReconnect()
            .build()

        notificationHub.onreconnecting(error => console.log('GroupAdminNotifications.Dropped: ', error))
        notificationHub.onreconnected(() => console.log('GroupAdminNotifications.Restored'))
        notificationHub.onclose(error => console.log('GroupAdminNotifications.Closed: ', error))
    
        notificationHub.on('*', (...args) => {
            console.log('SignalR received:', args)
        })
        
        notificationHub.on('User.Changed', (args: ItemAddedNotificationArgs) => {
            console.log(`GroupAdmin.User.Changed: ${JSON.stringify(args)}`)
            // reload user ONLY if user is already present (not necessarily loaded)
            if (userStore.asyncUsers.get(args.itemId)) {
                userStore.getAsyncUser(args.itemId, LoadMode.Reload)
            }
        })

        notificationHub.on('DataGroup.Merged', (args: DataGroupMergedArgs) => {
            console.log(`GroupAdmin.DataGroup.Merged: ${JSON.stringify(args)}`)
            listenerSets.get('DataGroup.Merged')?.forEach(ls => ls(args))
        })

        notificationHub.on('DataGroupMember.OwnersAdded', async (args: DataGroupMemberOwnersAddedArgs) => {
            console.log(`GroupAdmin.DataGroupMember.OwnersAdded: ${JSON.stringify(args)}`)
            // load members whether data group is present or not (usually not)
            if (args.memberKeys.length > 0) {
                const requestTime = DateTime.utc()
                let dms: DataGroupMember[]
                if (args.memberKeys.length > 1 && args?.ownerId) {
                    // just get all members for the user at once instead (NOTE: related user is NOT reloaded)
                    dms = await dataGroupMemberStore.getMembersForUsersAsync([args.ownerId], LoadMode.Reload)
                }
                else {
                    // get each member one at a time (NOTE: related principals are NOT reloaded)
                    const asyncMembers = args.memberKeys.map(k => dataGroupMemberStore.getAsyncMemberByKey(k, LoadMode.Reload))
                    dms = (await Promise.all(asyncMembers.map(a => a.whenLoadCompleted!))).filter(isDefined)
                }
                dataGroupMemberStore.addToLoadedKeyLists(dms, requestTime)
                AsyncData.invalidate(dataGroupStore.asyncVisibleDataGroupKeys.get('all'))
                AsyncData.invalidate(userStore.asyncDataGroupMemberKeys.get(args?.ownerId ?? ''))
            }
        })

        notificationHub.on('FamilyGroupMember.Added', async (args: ItemAddedNotificationArgs) => {
            console.log(`GroupAdmin.FamilyGroupMember.Added: ${JSON.stringify(args)}`)
            const requestTime = DateTime.utc()
            const fm = await familyGroupMemberStore.getMemberAsync(args.itemId)
            if (fm) {
                familyGroupMemberStore.addToLoadedKeyLists([fm], requestTime)
            }
        })

        notificationHub.on('AuthCode.Used', (args: AuthCodeUsedArgs) => {
            console.log(`GroupAdmin.AuthCode.Used: ${JSON.stringify(args)}`)
            listenerSets.get('AuthCode.Used')?.forEach(ls => ls(args))
        })

        notificationHub.on('App.Ready', () => {
            console.log('GroupAdmin.App.Ready')
            listenerSets.get('App.Ready')?.forEach(ls => ls())
        })
        
        onUnmounted(() => {
            if (notificationHub) {
                notificationHub.stop()
                notificationHub = undefined
            }
        })    

        watch(() => tokenManager.current.userId, accessTokenChanged)

        console.log('GroupAdminNotifications.Initialized')

        accessTokenChanged()
    }
    
    function accessTokenChanged() {
        const tokenManager = useTokenManager()
        if (tokenManager.current.isValid && notificationHub?.state == 'Disconnected') {
            notificationHub.start()
                .then(() => console.log('GroupAdminNotifications.Started'))
                .catch(err => console.error('GroupAdminNotifications.StartError:', err))
        }
        if (!tokenManager.current.isValid && notificationHub?.state == 'Connected') {
            notificationHub.stop()
                .then(() => console.log('GroupAdminNotifications.Stopped'))
                .catch(err => console.error('GroupAdminNotifications.StopError:', err))
        }
    }

    const getConnectionId = () => notificationHub?.connectionId

    function addListener(methodName: string, listener: (args: any) => void) {
        if (!listenerSets.has(methodName)) {
            listenerSets.set(methodName, new Set())
        }
        listenerSets.get(methodName)!.add(listener)
        localListeners.push(listener)
    }
    
    function onDataGroupMerged(listener: (args: DataGroupMergedArgs) => void) {
        addListener('DataGroup.Merged', listener)
    }

    function onAuthCodeUsed(listener: (args: AuthCodeUsedArgs) => void) {
        addListener('AuthCode.Used', listener)
    }

    function onAppReady(listener: () => void) {
        addListener('App.Ready', listener)
    }

    onUnmounted(() => {
        // remove all local listeners
        localListeners.forEach(ls => {
            listenerSets.forEach((s: Set<Listener>) => {
                s.delete(ls) // if present
            })
        })
    })

    return {
        initialize,
        accessTokenChanged,
        getConnectionId,
        onDataGroupMerged,
        onAuthCodeUsed,
        onAppReady,
    }
}

interface DataGroupMergedArgs {
    groupId: string
    intoGroupId: string
}

interface DataGroupMemberOwnersAddedArgs {
    ownerId: string
    memberKeys: string[]
}

interface AuthCodeUsedArgs {
    clientId: string
    notificationId: string
}
